import { useEffect, useState } from "react";
import { Loader2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const waNumber = (phone) => {
  let n = (phone || "").replace(/[^0-9+]/g, "");
  if (n.startsWith("+")) n = n.slice(1);
  else if (n.startsWith("00")) n = n.slice(2);
  else if (n.startsWith("3")) n = `39${n}`;
  return n;
};

export const DeadlineReminderDialog = ({ target, onClose, onSent }) => {
  const [info, setInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const channel = target?.channel;
  const isMail = channel === "email";

  useEffect(() => {
    if (!target) return;
    setInfo(null);
    api.get(`/policies/${target.row.id}/deadline-reminder`)
      .then(({ data }) => { setInfo(data); setSubject(data.subject); setText(data.text); })
      .catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [target, onClose]);

  const dest = isMail ? info?.email : info?.phone;

  const send = async () => {
    setSending(true);
    try {
      if (!isMail) window.open(`https://wa.me/${waNumber(dest)}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      const { data } = await api.post(`/policies/${target.row.id}/deadline-reminder`, { channel, subject, text });
      toast.success(isMail ? `Email inviata a ${dest}` : "WhatsApp aperto con il messaggio");
      onSent(target.row.id, data.last_reminder);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="deadline-reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isMail ? <Mail className="h-5 w-5 text-blue-600" /> : <MessageCircle className="h-5 w-5 text-emerald-600" />}
            Promemoria scadenza {isMail ? "via email" : "via WhatsApp"}
          </DialogTitle>
        </DialogHeader>
        {!info ? (
          <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm" data-testid="deadline-reminder-dest">
              <span className="text-slate-500">A: </span><b>{info.client_name}</b>{" "}
              {dest ? <span className="text-slate-600">({dest})</span> : <span className="text-red-600 font-medium">— {isMail ? "nessuna email in anagrafica" : "nessun numero in anagrafica"}</span>}
            </div>
            {isMail && <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto" data-testid="deadline-reminder-subject" />}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} className="text-sm" data-testid="deadline-reminder-text" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="deadline-reminder-cancel">Annulla</Button>
          <Button onClick={send} disabled={!info || !dest || !text.trim() || sending} data-testid="deadline-reminder-send"
            className={isMail ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}>
            {sending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isMail ? "Invia email" : "Apri WhatsApp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
